import { Injectable } from '@angular/core';
import { environment } from '../../enviroment/enviroment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

interface PaymentLinkResponse {
  payment_url: string;
  preference_id: string;
  reservation_id: string;
  status: string;
  message?: string;
}

interface PaymentStatusResponse {
  reservation_id: string;
  payment: {
    status: string;
    status_description: string;
    mp_preference_id?: string;
    mp_payment_id?: string;
    payment_method?: string;
    amount?: number;
    created_date?: string;
    updated_date?: string;
  };
  can_retry?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MercadoPagoService {
  private readonly mercadoPagoUrl = environment.mercadoPagoUrl;     // POST: Generar link de pago ⭐
  private readonly endingPaymentUrl = environment.endingPaymentUrl; // Webhook de MercadoPago

  constructor(private readonly http: HttpClient) {}

  /**
   * Crear link de pago de MercadoPago para una reserva
   */
  createPaymentLink(reservation_id: string, accessToken: string): Observable<PaymentLinkResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    const body = { reservation_id };

    return this.http.post<PaymentLinkResponse>(this.mercadoPagoUrl, body, { headers });
  }

  /**
   * Verificar estado de pago de una reserva
   */
  getPaymentStatus(reservation_id: string): Observable<PaymentStatusResponse> {
    return this.http.get<PaymentStatusResponse>(`${this.endingPaymentUrl}?reservation_id=${reservation_id}`);
  }

  /**
   * Abrir ventana de pago de MercadoPago
   */
  openPaymentWindow(paymentUrl: string): Window {
    const paymentWindow = window.open(
      paymentUrl,
      'mercadopago-payment',
      'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
    );

    if (!paymentWindow) {
      throw new Error('No se pudo abrir la ventana de pago. Verifica que no esté siendo bloqueada por el navegador.');
    }

    return paymentWindow;
  }

  /**
   * Monitorear el estado de la ventana de pago
   */
  monitorPaymentWindow(paymentWindow: Window, reservation_id: string, onStatusChange: (status: any) => void): void {
    const checkInterval = setInterval(() => {
      // Verificar si la ventana fue cerrada
      if (paymentWindow.closed) {
        clearInterval(checkInterval);
        // Verificar el estado final del pago
        this.getPaymentStatus(reservation_id).subscribe({
          next: (status) => {
            onStatusChange(status);
          },
          error: (error) => {
            console.error('Error verificando estado de pago:', error);
            onStatusChange({ error: 'Error verificando el estado del pago' });
          }
        });
      }
    }, 1000); // Verificar cada segundo

    // Timeout de seguridad (15 minutos)
    setTimeout(() => {
      if (!paymentWindow.closed) {
        clearInterval(checkInterval);
        paymentWindow.close();
        onStatusChange({ timeout: true, message: 'Tiempo de pago agotado' });
      }
    }, 15 * 60 * 1000);
  }

  /**
   * Procesar pago completo (crear link + abrir ventana + monitorear)
   */
  processPayment(
    reservation_id: string,
    accessToken: string,
    onSuccess: (result: any) => void,
    onError: (error: any) => void,
    onStatusChange?: (status: any) => void
  ): void {
    // Paso 1: Crear link de pago
    this.createPaymentLink(reservation_id, accessToken).subscribe({
      next: (linkResponse) => {
        try {
          // Paso 2: Abrir ventana de pago
          const paymentWindow = this.openPaymentWindow(linkResponse.payment_url);
          
          // Verificar que la ventana se abrió correctamente
          if (!paymentWindow) {
            onError({ message: 'No se pudo abrir la ventana de pago' });
            return;
          }
          
          // Paso 3: Monitorear la ventana
          this.monitorPaymentWindow(paymentWindow, reservation_id, (status) => {
            if (onStatusChange) {
              onStatusChange(status);
            }

            // Determinar si el pago fue exitoso
            if (status.payment?.status === 'Pagado') {
              onSuccess({
                payment_status: status.payment.status,
                payment_id: status.payment.mp_payment_id,
                preference_id: status.payment.mp_preference_id
              });
            } else if (status.payment?.status === 'Pago rechazado') {
              onError({
                message: 'El pago fue rechazado',
                status: status.payment.status,
                can_retry: status.can_retry
              });
            } else if (status.error || status.timeout) {
              onError(status);
            }
            // Si está en proceso, esperar más actualizaciones
          });

        } catch (error: any) {
          onError({ message: error.message });
        }
      },
      error: (error) => {
        onError({
          message: 'Error creando el link de pago',
          details: error.error?.message || error.message
        });
      }
    });
  }
}
